import cn from 'classnames';
import styles from './icon.module.sass';
import * as Icons from '@intigral/icons';

export const fontIcons = [
    'account',
    'account-circle',
    'agreement',
    'alarm',
    'alert',
    'apps',
    'arrow-down',
    'arrow-down-lg',
    'arrow-left',
    'arrow-left-lg',
    'arrow-right',
    'arrow-right-lg',
    'arrow-up',
    'arrow-up-lg',
    'audio',
    'bell',
    'bell-add',
    'bell-add-circle',
    'bell-check',
    'bell-check-circle',
    'bell-filled',
    'bell-off',
    'bell-remove',
    'bell-ring',
    'bookmark',
    'business',
    'calendar',
    'card',
    'card-add',
    'cart',
    'cart-filled',
    'caret-down',
    'cast',
    'channel-lock',
    'channels',
    'chat',
    'check',
    'check-circle',
    'check-lg',
    'chevron-down',
    'chevron-down-lg',
    'chevron-left',
    'chevron-left-lg',
    'chevron-right',
    'chevron-right-lg',
    'chevron-up',
    'chevron-up-lg',
    'close',
    'close-circle',
    'close-lg',
    'close-sm',
    'comment',
    'copy',
    'cry',
    'devices',
    'devices-laptop',
    'diagnostic',
    'doc',
    'download',
    'download-filled',
    'download-loading',
    'edit',
    'episode-list',
    'episode-next',
    'episode-next-box',
    'episode-next-lg',
    'episode-same',
    'explore',
    'external-link',
    'eye',
    'eye-off',
    'facebook',
    'filter',
    'flag',
    'font-size',
    'forward-circle',
    'forward-plus',
    'forward-repeat',
    'forward10',
    'forward20',
    'forward30',
    'fullscreen',
    'fullscreen-exit',
    'game',
    'genres',
    'globe',
    'heart',
    'heart-filled',
    'heart-off',
    'help',
    'home',
    'home-filled',
    'image',
    'info',
    'instagram',
    'kids',
    'language',
    'laptop',
    'link',
    'list',
    'live',
    'live-filled',
    'live-label',
    'live-label-filled',
    'live-tv',
    'lock',
    'lock-circle',
    'lock-filled',
    'logout',
    'mail',
    'menu',
    'menu-left',
    'menu-right',
    'minus',
    'minus-circle',
    'mobile',
    'more',
    'more-circle',
    'more-vertical',
    'movie',
    'news',
    'operator',
    'pause',
    'pause-circle',
    'payments',
    'pencil',
    'pin-code',
    'pin-screen',
    'play',
    'play-circle',
    'play-filled',
    'plus',
    'plus-circle',
    'power',
    'privacy',
    'profile',
    'providers',
    'qitaf',
    'ramadan',
    'refresh',
    'reminder',
    'reminder-set',
    'replay',
    'replay-minus',
    'replay10',
    'replay20',
    'replay30',
    'restart',
    'reward',
    'rewind-circle',
    'sar',
    'screen',
    'screen-collapse',
    'screen-expand',
    'search',
    'search-filled',
    'search-history',
    'send',
    'settings',
    'settings-playback',
    'share',
    'similar',
    'skip-previous',
    'sms',
    'snapchat',
    'soccer',
    'sort',
    'sort-lines',
    'star',
    'star-filled',
    'star-off',
    'start-forward',
    'start-over',
    'stats',
    'stats-box',
    'stc',
    'store',
    'store-filled',
    'subscriptions',
    'swap',
    'team',
    'thumb-down',
    'thumb-down-filled',
    'thumb-up',
    'thumb-up-filled',
    'ticket',
    'timer',
    'titles-lock',
    'trash',
    'trophy',
    'trophy-filled',
    'tune',
    'tune-horizontal',
    'tv-guide',
    'tv-show',
    'tv-show-filled',
    'undo',
    'unlock',
    'volume-high',
    'volume-low',
    'volume-medium',
    'volume-off',
    'wifi',
    'wifi-alert',
    'wifi-dot',
    'x',
    'youtube',
] as const;

export type FontIconName = (typeof fontIcons)[number];

export type FontIconColor = 'gray' | 'green' | 'orange' | 'red' | '';
export type FontIconSize = 'x-small' | 'smaller' | 'small' | 'medium' | 'large' | 'size60' | 'x-large';

interface FontIconProps {
    name: FontIconName;
    color?: FontIconColor;
    size?: FontIconSize;
    md?: FontIconSize;
    rotate?: 'left' | 'right' | 'flip' | 'partial' | null;
    className?: string;
}

export const FontIcon = ({
    name,
    color = '',
    size = 'smaller',
    md = 'small',
    rotate = null,
    className,
}: FontIconProps) => {
    const isUsingReactComponents = true;

    if (isUsingReactComponents) {
        const pascalCaseName = toPascalCase(name);
        const Icon = Icons[pascalCaseName];

        if (!Icon) {
            // dev marker: find gaps via [data-missing-icon]
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.error(
                    `[FontIcon] no icon for name "${name}" (looked up "${pascalCaseName}" in @intigral/icons). ` +
                        `It renders nothing. Either the name is stale or the set was generated without it.`
                );
                return <span data-missing-icon={name} />;
            }
            return null;
        }

        return <Icon size={size} md={md} color={color} rotate={rotate} className={className} />;
    }

    return (
        <span
            className={cn(
                styles.icon,
                styles[color],
                styles[size],
                styles['md-' + md],
                styles['name-' + name],
                rotate && styles['rotate-' + rotate],
                className
            )}
        ></span>
    );
};

// https://en.wiktionary.org/wiki/Pascal_case
function toPascalCase(string: string) {
    return `${string}`
        .toLowerCase()
        .replace(new RegExp(/[-_]+/, 'g'), ' ')
        .replace(new RegExp(/[^\w\s]/, 'g'), '')
        .replace(new RegExp(/\s+(.)(\w*)/, 'g'), ($1, $2, $3) => `${$2.toUpperCase() + $3}`)
        .replace(new RegExp(/\w/), (s) => s.toUpperCase());
}
